import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, Globe, Shield, FileCheck, ArrowRight, AlertCircle } from 'lucide-react';

const ProductClassification = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Product Classification<br />Services
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Accurate classification across HTS, HS, ECCN, and USML for global compliance. Minimize duty exposure and ensure regulatory adherence across all jurisdictions.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Request Classification Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Classification Systems Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Classification Expertise
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl">
              Government authorities worldwide expect accuracy in product classification. Audits demand it. Errors result in penalties, delays, and reputational risk. Our professionals ensure your practices comply across all regulatory frameworks, whether you're importing, exporting, or both.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* HTS/HS */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <Globe className="h-7 w-7 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">HTS & HS Codes</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                <strong>Harmonized Tariff Schedule (HTS)</strong> and <strong>Harmonized System (HS)</strong> classification for accurate tariff determination and duty optimization.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">U.S. HTS (10-digit) and Schedule B classification</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Global HS classification for 190+ countries</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Duty optimization and tariff engineering</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Binding ruling requests and appeals</span>
                </li>
              </ul>
            </div>

            {/* ECCN */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ECCN Classification</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                <strong>Export Control Classification Numbers (ECCN)</strong> for dual-use items under the Commerce Control List and Export Administration Regulations.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">BIS jurisdiction determination</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">CCATS (Commodity Classification) requests</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">EAR99 vs. controlled item analysis</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">600 series and 9x515 classification</span>
                </li>
              </ul>
            </div>

            {/* USML */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <FileCheck className="h-7 w-7 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">USML & ITAR</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                <strong>United States Munitions List (USML)</strong> classification for defense articles and services under ITAR jurisdiction.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">DDTC jurisdiction determination</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Commodity Jurisdiction (CJ) requests</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">ITAR vs. EAR determination</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Technical data and defense service classification</span>
                </li>
              </ul>
            </div>

            {/* Global Jurisdictions */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <Globe className="h-7 w-7 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Global Export Controls</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                International export control classification for <strong>EU, UK, Canada, Australia,</strong> and other jurisdictions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">EU Dual-Use Regulation classification</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">UK Strategic Export Controls</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Wassenaar Arrangement alignment</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Multi-jurisdictional compliance strategies</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Classification Matters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Why Accurate Classification is Critical
            </h2>
            <p className="text-lg text-gray-600">
              Misclassification carries serious consequences beyond financial penalties
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <AlertCircle className="h-10 w-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Financial Risk</h3>
              <p className="text-gray-600 leading-relaxed">
                Incorrect HTS classification can result in overpaid duties, underpaid assessments, or significant penalties during customs audits. ECCN/USML errors may trigger export violations with fines exceeding $1 million per incident.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <AlertCircle className="h-10 w-10 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Operational Delays</h3>
              <p className="text-gray-600 leading-relaxed">
                Shipments can be detained at borders pending classification review. Export license applications may be delayed or denied. Supply chain disruptions cascade to customers and production schedules.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <AlertCircle className="h-10 w-10 text-yellow-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Liability</h3>
              <p className="text-gray-600 leading-relaxed">
                Company officers and compliance personnel may face personal liability for willful violations. Repeated misclassification triggers enhanced scrutiny and potential debarment from export privileges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Methodology */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Our Classification Methodology
          </h2>

          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Product Analysis & Documentation Review</h3>
                <p className="text-gray-600 leading-relaxed">
                  Comprehensive technical analysis of product specifications, materials, functionality, and intended use. Review of engineering drawings, datasheets, software documentation, and manufacturing processes to establish accurate baseline data.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Regulatory Framework Determination</h3>
                <p className="text-gray-600 leading-relaxed">
                  Systematic evaluation against USML, Commerce Control List, and HTS Schedule. Application of jurisdictional tests, specifically designed criteria, and catch-all provisions. Cross-referencing with international control lists for multi-jurisdictional compliance.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Classification Assignment & Documentation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Definitive classification assignments with detailed technical justifications. Preparation of classification rationale memoranda suitable for audit defense. Coordination of official classification ruling requests (CCATS, CJ, binding rulings) when appropriate.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance Integration & Monitoring</h3>
                <p className="text-gray-600 leading-relaxed">
                  Integration of classifications into ERP systems, trade management platforms, and shipping documentation. Establishment of change control procedures for product modifications. Ongoing monitoring of regulatory amendments affecting existing classifications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ensure Accurate Classification Across All Jurisdictions
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Partner with experts who understand the complexity of global product classification requirements.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule a Classification Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductClassification;
